import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import commandService, { Command, CommandsResponse } from '../../services/commandService';

const Commands: React.FC = () => {
  const [commands, setCommands] = useState<CommandsResponse>({
    instructions: [],
    reasons: [],
    problems: []
  });
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<{ id: string; text: string; type: 'instruction' | 'reason' | 'problem' } | null>(null);
  const [newInstruction, setNewInstruction] = useState('');
  const [newReason, setNewReason] = useState('');
  const [newProblem, setNewProblem] = useState('');
  const [addingInstruction, setAddingInstruction] = useState(false);
  const [addingReason, setAddingReason] = useState(false);
  const [addingProblem, setAddingProblem] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    loadCommands();
  }, []);

  const loadCommands = async () => {
    try {
      setLoading(true);
      const data = await commandService.getCommands();
      setCommands(data);
    } catch (error) {

      toast.error('خطا در بارگیری دستورالعمل‌ها');
    } finally {
      setLoading(false);
    }
  };

  const handleAddInstruction = async () => {
    if (submittingRef.current) return;
    if (!newInstruction.trim()) {
      toast.error('متن دستورالعمل نمی‌تواند خالی باشد');
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      await commandService.createCommand({
        type: 'instruction',
        text: newInstruction.trim()
      });
      setNewInstruction('');
      setAddingInstruction(false);
      await loadCommands();
      toast.success('دستورالعمل با موفقیت اضافه شد');
    } catch (error: any) {

      toast.error(error.message || 'خطا در افزودن دستورالعمل');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleAddReason = async () => {
    if (submittingRef.current) return;
    if (!newReason.trim()) {
      toast.error('متن دلیل نمی‌تواند خالی باشد');
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      await commandService.createCommand({
        type: 'reason',
        text: newReason.trim()
      });
      setNewReason('');
      setAddingReason(false);
      await loadCommands();
      toast.success('دلیل با موفقیت اضافه شد');
    } catch (error: any) {

      toast.error(error.message || 'خطا در افزودن دلیل');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleAddProblem = async () => {
    if (submittingRef.current) return;
    if (!newProblem.trim()) {
      toast.error('متن دلیل تصویر جواب آزمایش نمی‌تواند خالی باشد');
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      await commandService.createCommand({
        type: 'problem',
        text: newProblem.trim()
      });
      setNewProblem('');
      setAddingProblem(false);
      await loadCommands();
      toast.success('دلیل تصویر جواب آزمایش با موفقیت اضافه شد');
    } catch (error: any) {

      toast.error(error.message || 'خطا در افزودن دلیل تصویر جواب آزمایش');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleEdit = (command: Command) => {
    setEditingItem({
      id: command._id,
      text: command.text,
      type: command.type
    });
  };

  const handleSaveEdit = async () => {
    if (!editingItem || !editingItem.text.trim()) {
      toast.error('متن نمی‌تواند خالی باشد');
      return;
    }

    try {
      await commandService.updateCommand(editingItem.id, {
        text: editingItem.text.trim()
      });
      setEditingItem(null);
      await loadCommands();
      toast.success('تغییرات با موفقیت ذخیره شد');
    } catch (error: any) {

      toast.error(error.message || 'خطا در بروزرسانی');
    }
  };

  const handleDelete = async (id: string, type: 'instruction' | 'reason' | 'problem') => {
    const confirmMessage = type === 'instruction' 
      ? 'آیا از حذف این دستورالعمل اطمینان دارید؟'
      : type === 'reason'
      ? 'آیا از حذف این دلیل اطمینان دارید؟'
      : 'آیا از حذف این دلیل تصویر جواب آزمایش اطمینان دارید؟';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await commandService.deleteCommand(id);
      await loadCommands();
      toast.success('حذف با موفقیت انجام شد');
    } catch (error: any) {

      toast.error(error.message || 'خطا در حذف');
    }
  };

  const cancelEdit = () => {
    setEditingItem(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" dir="rtl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">مدیریت دستورالعمل‌ها و دلایل</h1>
        <p className="text-gray-600">مدیریت دستورالعمل‌ها و دلایل سیستم</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Instructions Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-blue-600 flex items-center">
              <svg className="w-6 h-6 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              دستورالعمل‌ها
            </h2>
            <button
              onClick={() => setAddingInstruction(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center"
            >
              <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              افزودن
            </button>
          </div>

          {/* Add new instruction */}
          {addingInstruction && (
            <div className="mb-4 p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
              <textarea
                value={newInstruction}
                onChange={(e) => setNewInstruction(e.target.value)}
                placeholder="متن دستورالعمل جدید را وارد کنید..."
                className="w-full p-3 border border-gray-300 rounded-lg resize-none h-20 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                dir="rtl"
              />
              <div className="flex justify-end mt-3 space-x-2 space-x-reverse">
                <button
                  onClick={() => {
                    setAddingInstruction(false);
                    setNewInstruction('');
                  }}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200"
                >
                  انصراف
                </button>
                <button
                  onClick={handleAddInstruction}
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'در حال ارسال...' : 'افزودن'}
                </button>
              </div>
            </div>
          )}

          {/* Instructions list */}
          <div className="space-y-3">
            {commands.instructions.map((instruction, index) => (
              <div key={instruction._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full ml-2">
                        {index + 1}
                      </span>
                      <span className="text-sm text-gray-500">دستورالعمل</span>
                    </div>
                    {editingItem?.id === instruction._id ? (
                      <div>
                        <textarea
                          value={editingItem.text}
                          onChange={(e) => setEditingItem({ ...editingItem, text: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-lg resize-none h-20 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          dir="rtl"
                        />
                        <div className="flex justify-end mt-3 space-x-2 space-x-reverse">
                          <button
                            onClick={cancelEdit}
                            className="px-3 py-1 text-sm text-gray-600 bg-gray-200 rounded hover:bg-gray-300 transition-colors duration-200"
                          >
                            انصراف
                          </button>
                          <button
                            onClick={handleSaveEdit}
                            className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors duration-200"
                          >
                            ذخیره
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-700 leading-relaxed fixed-p">{instruction.text}</p>
                    )}
                  </div>
                  {editingItem?.id !== instruction._id && (
                    <div className="flex space-x-1 space-x-reverse mr-3">
                      <button
                        onClick={() => handleEdit(instruction)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                        title="ویرایش"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(instruction._id, 'instruction')}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200"
                        title="حذف"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {commands.instructions.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p>هیچ دستورالعملی ثبت نشده است</p>
              </div>
            )}
          </div>
        </div>

        {/* Reasons Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-red-600 flex items-center">
              <svg className="w-6 h-6 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              دلایل
            </h2>
            <button
              onClick={() => setAddingReason(true)}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center"
            >
              <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              افزودن
            </button>
          </div>

          {/* Add new reason */}
          {addingReason && (
            <div className="mb-4 p-4 border-2 border-red-200 rounded-lg bg-red-50">
              <textarea
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                placeholder="متن دلیل جدید را وارد کنید..."
                className="w-full p-3 border border-gray-300 rounded-lg resize-none h-20 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                dir="rtl"
              />
              <div className="flex justify-end mt-3 space-x-2 space-x-reverse">
                <button
                  onClick={() => {
                    setAddingReason(false);
                    setNewReason('');
                  }}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200"
                >
                  انصراف
                </button>
                <button
                  onClick={handleAddReason}
                  disabled={submitting}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'در حال ارسال...' : 'افزودن'}
                </button>
              </div>
            </div>
          )}

          {/* Reasons list */}
          <div className="space-y-3">
            {commands.reasons.map((reason, index) => (
              <div key={reason._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full ml-2">
                        {index + 1}
                      </span>
                      <span className="text-sm text-gray-500">دلیل</span>
                    </div>
                    {editingItem?.id === reason._id ? (
                      <div>
                        <textarea
                          value={editingItem.text}
                          onChange={(e) => setEditingItem({ ...editingItem, text: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-lg resize-none h-20 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          dir="rtl"
                        />
                        <div className="flex justify-end mt-3 space-x-2 space-x-reverse">
                          <button
                            onClick={cancelEdit}
                            className="px-3 py-1 text-sm text-gray-600 bg-gray-200 rounded hover:bg-gray-300 transition-colors duration-200"
                          >
                            انصراف
                          </button>
                          <button
                            onClick={handleSaveEdit}
                            className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors duration-200"
                          >
                            ذخیره
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-700 leading-relaxed  fixed-p">{reason.text}</p>
                    )}
                  </div>
                  {editingItem?.id !== reason._id && (
                    <div className="flex space-x-1 space-x-reverse mr-3">
                      <button
                        onClick={() => handleEdit(reason)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                        title="ویرایش"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(reason._id, 'reason')}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200"
                        title="حذف"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {commands.reasons.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p>هیچ دلیلی ثبت نشده است</p>
              </div>
            )}
          </div>
        </div>

        {/* Problems Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-orange-600 flex items-center">
              <svg className="w-6 h-6 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              دلایل تصویر جواب آزمایش
            </h2>
            <button
              onClick={() => setAddingProblem(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center"
            >
              <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              افزودن
            </button>
          </div>

          {/* Add new problem */}
          {addingProblem && (
            <div className="mb-4 p-4 border-2 border-orange-200 rounded-lg bg-orange-50">
              <textarea
                value={newProblem}
                onChange={(e) => setNewProblem(e.target.value)}
                placeholder="متن دلیل جدید تصویر جواب آزمایش را وارد کنید..."
                className="w-full p-3 border border-gray-300 rounded-lg resize-none h-20 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                dir="rtl"
              />
              <div className="flex justify-end mt-3 space-x-2 space-x-reverse">
                <button
                  onClick={() => {
                    setAddingProblem(false);
                    setNewProblem('');
                  }}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200"
                >
                  انصراف
                </button>
                <button
                  onClick={handleAddProblem}
                  disabled={submitting}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'در حال ارسال...' : 'افزودن'}
                </button>
              </div>
            </div>
          )}

          {/* Problems list */}
          <div className="space-y-3">
            {(commands.problems || []).map((problem, index) => (
              <div key={problem._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2.5 py-0.5 rounded-full ml-2">
                        {index + 1}
                      </span>
                      <span className="text-sm text-gray-500">دلیل تصویر جواب</span>
                    </div>
                    {editingItem?.id === problem._id ? (
                      <div>
                        <textarea
                          value={editingItem.text}
                          onChange={(e) => setEditingItem({ ...editingItem, text: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-lg resize-none h-20 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          dir="rtl"
                        />
                        <div className="flex justify-end mt-3 space-x-2 space-x-reverse">
                          <button
                            onClick={cancelEdit}
                            className="px-3 py-1 text-sm text-gray-600 bg-gray-200 rounded hover:bg-gray-300 transition-colors duration-200"
                          >
                            انصراف
                          </button>
                          <button
                            onClick={handleSaveEdit}
                            className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors duration-200"
                          >
                            ذخیره
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-700 leading-relaxed  fixed-p">{problem.text}</p>
                    )}
                  </div>
                  {editingItem?.id !== problem._id && (
                    <div className="flex space-x-1 space-x-reverse mr-3">
                      <button
                        onClick={() => handleEdit(problem)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                        title="ویرایش"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(problem._id, 'problem')}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200"
                        title="حذف"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {(commands.problems || []).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p>هیچ دلیل تصویر جواب آزمایشی ثبت نشده است</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Commands; 